import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radio, ChevronRight, Clock, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

function LiveMatchItem({ match }) {
  const isLive = [2, 3, 4].includes(match.state_id);
  const isFinished = match.state_id === 5;
  const isUpcoming = match.state_id === 1;

  return (
    <Link
      to={`/matcher/${match.fixture_id}`}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all shrink-0 min-w-[260px] max-w-[300px] ${
        isLive
          ? "bg-green-500/10 border border-green-400/30 hover:bg-green-500/20"
          : "bg-white/5 border border-white/10 hover:bg-white/10"
      }`}
      data-testid={`live-ticker-match-${match.fixture_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          {match.home_logo && <img src={match.home_logo} alt="" className="w-4 h-4 object-contain" />}
          <span className={`text-xs truncate ${match.home_goals > match.away_goals ? "font-bold text-white" : "text-slate-300"}`}>
            {match.home_team}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {match.away_logo && <img src={match.away_logo} alt="" className="w-4 h-4 object-contain" />}
          <span className={`text-xs truncate ${match.away_goals > match.home_goals ? "font-bold text-white" : "text-slate-300"}`}>
            {match.away_team}
          </span>
        </div>
      </div>
      <div className="text-right shrink-0">
        {isUpcoming ? (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {(() => {
              const d = new Date(match.date);
              const now = new Date();
              const isToday = d.toDateString() === now.toDateString();
              if (isToday) return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
              return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
            })()}
          </span>
        ) : (
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end">
              <span className="text-sm font-bold text-white">{match.home_goals}</span>
              <span className="text-xs text-slate-500">-</span>
              <span className="text-sm font-bold text-white">{match.away_goals}</span>
            </div>
            {isLive && (
              <span className="text-[10px] text-green-400 font-bold">{match.minute}'</span>
            )}
            {isFinished && (
              <span className="text-[10px] text-slate-500">FT</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function LiveMatchTicker() {
  const [liveMatches, setLiveMatches] = useState([]);
  const [todayMatches, setTodayMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [liveRes, todayRes] = await Promise.allSettled([
        axios.get(`${API}/api/livescores`),
        axios.get(`${API}/api/livescores/today`),
      ]);
      if (liveRes.status === "fulfilled") setLiveMatches(liveRes.value.data.matches || []);
      if (todayRes.status === "fulfilled") setTodayMatches(todayRes.value.data.matches || []);

      // If no live or today matches, fetch upcoming from fixtures DB
      const live = liveRes.status === "fulfilled" ? liveRes.value.data.matches : [];
      const today = todayRes.status === "fulfilled" ? todayRes.value.data.matches : [];
      if (live.length === 0 && today.length === 0) {
        const upRes = await axios.get(`${API}/api/fixtures?season=2026&status=NS&sort_order=asc&per_page=6`);
        const fixtures = upRes.data.fixtures || [];
        setUpcomingMatches(fixtures.map(f => ({
          fixture_id: f.sportmonks_fixture_id || f.fixture_id,
          home_team: f.home_team,
          away_team: f.away_team,
          home_logo: f.home_logo,
          away_logo: f.away_logo,
          date: f.date,
          state_id: 1,
          league: f.division,
        })));
      }
    } catch (e) {
      // Silently fail — ticker is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh: 30s for live, 120s otherwise
  useEffect(() => {
    const hasLive = liveMatches.length > 0;
    const interval = setInterval(fetchData, hasLive ? 30000 : 120000);
    return () => clearInterval(interval);
  }, [liveMatches.length, fetchData]);

  // Merge and deduplicate
  const liveIds = new Set(liveMatches.map(m => m.fixture_id));
  const allMatches = liveMatches.length > 0 || todayMatches.length > 0
    ? [...liveMatches, ...todayMatches.filter(m => !liveIds.has(m.fixture_id))]
    : upcomingMatches;
  const hasLive = liveMatches.length > 0;
  const isUpcomingMode = liveMatches.length === 0 && todayMatches.length === 0 && upcomingMatches.length > 0;

  // Always reserve space to prevent CLS
  if (!loading && allMatches.length === 0) return <div className="h-[52px] bg-[#0F172A]/80 border-b border-white/5" />;
  if (loading) return <div className="h-[52px] bg-[#0F172A]/80 border-b border-white/5" />;

  return (
    <div className={`${hasLive ? "bg-gradient-to-r from-green-900/80 to-emerald-900/60" : "bg-[#0F172A]/80"} backdrop-blur border-b border-white/5`} data-testid="live-match-ticker">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
        <div className="flex items-center gap-4">
          {/* Label */}
          <Link to="/live" className="flex items-center gap-2 shrink-0">
            {hasLive ? (
              <Badge className="bg-green-500 text-white text-xs px-2 py-0.5 animate-pulse" data-testid="ticker-live-badge">
                <Radio className="w-3 h-3 mr-1" /> LIVE
              </Badge>
            ) : isUpcomingMode ? (
              <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs px-2 py-0.5">
                <Clock className="w-3 h-3 mr-1" /> Kommande
              </Badge>
            ) : (
              <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs px-2 py-0.5">
                <Clock className="w-3 h-3 mr-1" /> Idag
              </Badge>
            )}
          </Link>

          {/* Scrollable match list */}
          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2">
              {allMatches.map(m => (
                <LiveMatchItem key={m.fixture_id} match={m} />
              ))}
            </div>
          </div>

          {/* Link to full page */}
          <Link
            to="/live"
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors shrink-0 whitespace-nowrap"
            data-testid="ticker-see-all"
          >
            Alla <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
